import React, { lazy, Suspense, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Users, Shield, Compass } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useParent } from '../../context/ParentContext';
import { useModules } from '../../context/ModulesContext';
import api from '../../lib/api';
import { Tabs, TabsContent } from '../ui/tabs';
import { useMatchText } from '../match/workspaceShared';
import MobilePage, { MobilePageTabs } from './PlayerMobilePage';
import SquadMobileView from './SquadMobileView';

const Roles = lazy(() => import('../match/RoleLibrary'));
const Identity = lazy(() => import('../match/PlayingPrinciples'));

export default function PlayerSquadWorkspace() {
  const { activeTeam, user, isCoach, isPlayer } = useAuth();
  const { isParentView } = useParent();
  const { isModuleEnabled } = useModules();
  const [options, setOptions] = useState(null);
  const [dirty, setDirty] = useState(false);
  const scope = `${activeTeam?.id}:${user?.id}:${isParentView}`;
  useEffect(() => {
    let active = true;
    setOptions(null);
    setDirty(false);
    if (activeTeam?.id && !isParentView) {
      api.get(`/teams/${encodeURIComponent(activeTeam.id)}/match-workspace`)
        .then(({ data }) => { if (active) setOptions({ scope, data }); }).catch(() => {});
    }
    return () => { active = false; };
  }, [activeTeam?.id, user?.id, isParentView, scope]);
  // Same authority as the previous match workspace. Moving a tab does not
  // grant edit rights; the identity component also checks server capabilities.
  const canManageRoles = !isParentView && Boolean(isCoach || (options?.scope === scope && options.data?.can_manage_templates));
  const tr = useMatchText();
  const [params, setParams] = useSearchParams();
  const items = [
    { value: 'squad', label: tr('Truppen', 'Squad'), icon: Users },
    ...(isModuleEnabled('tactic') ? [{ value: 'roles', label: tr('Roller', 'Roles'), icon: Shield }] : []),
    ...(isModuleEnabled('match') ? [{ value: 'identity', label: tr('Vår identitet', 'Our identity'), icon: Compass }] : []),
  ];
  const requested = params.get('view');
  const view = items.some(item => item.value === requested) ? requested : 'squad';
  const changeTab = next => {
    if (next === view) return;
    if (dirty && !window.confirm(tr('Byta flik med osparade ändringar?', 'Switch tabs with unsaved changes?'))) return;
    const query = new URLSearchParams(params);
    if (next === 'squad') query.delete('view'); else query.set('view', next);
    setParams(query);
    setDirty(false);
  };
  return <Tabs value={view} onValueChange={changeTab}>
    <MobilePage title={tr('Trupp', 'Squad')} subtitle={tr('Vårt lag, våra roller och vår identitet.', 'Our team, our roles and our identity.')}
      tabs={<MobilePageTabs items={items} label={tr('Truppens flikar', 'Squad sections')} />} testId="player-squad-workspace">
      <TabsContent value="squad"><SquadMobileView embedded /></TabsContent>
      <Suspense fallback={<p className="py-4 text-sm text-muted-foreground" role="status">{tr('Laddar…', 'Loading…')}</p>}>
        {isModuleEnabled('tactic') && <TabsContent value="roles">
          {activeTeam?.id && <div className="tn-player-role-library"><Roles key={scope} teamId={activeTeam.id} isCoach={canManageRoles} /></div>}
        </TabsContent>}
        {isModuleEnabled('match') && <TabsContent value="identity">
          {activeTeam?.id && <Identity key={scope} teamId={activeTeam.id} userId={user?.id} readOnly={isParentView || isPlayer} onDirtyChange={setDirty} />}
        </TabsContent>}
      </Suspense>
    </MobilePage>
  </Tabs>;
}
